/**
 * RepConnect - Healthcare Professional Dining Preferences
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Coffee, Plus, X, Utensils, CheckCircle, AlertTriangle, Heart, Ban, BookMarked, Edit
} from 'lucide-react';
import YelpRestaurantSearch from '../components/YelpRestaurantSearch';
import SavedOrderPopup from '../components/SavedOrderPopup';
import restaurantFavoritesService from '../services/restaurantFavoritesService';
import { savedOrdersService } from '../utils/savedOrdersService';
import { logger } from '../utils/logger';

const DiningPreferencesView = ({ currentUser }) => {
  const [saveStatus, setSaveStatus] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showYelpSearch, setShowYelpSearch] = useState(false);
  const [addingToList, setAddingToList] = useState(''); // 'favorites' or 'avoid'
  const [loading, setLoading] = useState(true);

  // Basic preferences (cuisines, dietary restrictions, etc.)
  const [preferences, setPreferences] = useState({
    cuisineTypes: [],
    dietaryRestrictions: [],
    allergens: [],
    deliveryPreference: 'Office delivery',
    specialRequests: ''
  });

  // Restaurant favorites loaded from database
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [avoidRestaurants, setAvoidRestaurants] = useState([]);

  // Saved orders
  const [savedOrders, setSavedOrders] = useState([]);
  const [showSavedOrderPopup, setShowSavedOrderPopup] = useState(false);
  const [editingSavedOrder, setEditingSavedOrder] = useState(null);
  const [editingRestaurant, setEditingRestaurant] = useState(null);

  const [newItem, setNewItem] = useState('');
  const [addingTo, setAddingTo] = useState('');
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      loadSavedPreferences();
      loadRestaurantFavorites();
      loadSavedOrders();
    }
  }, [currentUser]);

  // Auto-save preferences whenever they change
  useEffect(() => {
    if (!currentUser || loading) return;

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce auto-save by 1 second
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences(true); // Pass true to indicate auto-save
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [preferences, currentUser, loading]);

  const loadSavedPreferences = async () => {
    if (!currentUser) return;

    try {
      const { supabase } = await import('../utils/supabase');

      const { data, error } = await supabase
        .from('user_profiles')
        .select('dining_preferences')
        .eq('id', currentUser.id)
        .single();

      if (error) {
        logger.error('Error loading preferences from Supabase', { error: error.message, userId: currentUser.id });
      } else if (data && data.dining_preferences) {
        setPreferences({
          cuisineTypes: data.dining_preferences.cuisineTypes || [],
          dietaryRestrictions: data.dining_preferences.dietaryRestrictions || [],
          allergens: data.dining_preferences.allergens || [],
          deliveryPreference: data.dining_preferences.deliveryPreference || 'Office delivery',
          specialRequests: data.dining_preferences.specialRequests || ''
        });
      }
    } catch (error) {
      logger.error('Error loading preferences', { error: error.message, userId: currentUser?.id });
    }
  };

  const loadRestaurantFavorites = async (showLoadingSpinner = true) => {
    if (!currentUser) return;

    try {
      // Only show loading spinner on initial page load, not when refreshing after add
      if (showLoadingSpinner) {
        setLoading(true);
      }

      // Load favorites
      const favorites = await restaurantFavoritesService.getFavoriteRestaurants(currentUser.id);
      logger.debug('Favorites loaded from DB', { count: favorites?.length || 0 });
      setFavoriteRestaurants(favorites || []);

      // Load avoid list
      const avoid = await restaurantFavoritesService.getAvoidRestaurants(currentUser.id);
      logger.debug('Avoid list loaded from DB', { count: avoid?.length || 0 });
      setAvoidRestaurants(avoid || []);

    } catch (error) {
      logger.error('Error loading restaurant favorites', { error: error.message, userId: currentUser.id });
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  const loadSavedOrders = async () => {
    if (!currentUser) return;

    try {
      const orders = await savedOrdersService.getSavedOrdersForCustomer(currentUser.id);
      logger.debug('Saved orders loaded', { count: orders?.length || 0 });
      setSavedOrders(orders || []);
    } catch (error) {
      logger.error('Error loading saved orders', { error: error.message, userId: currentUser.id });
    }
  };

  const handleEditSavedOrder = (order) => {
    // Extract restaurant info from saved order
    const restaurant = {
      name: order.restaurant_name,
      address: order.restaurant_address,
      phone: order.restaurant_phone,
      yelp_business_id: order.restaurant_yelp_id,
      ...order.restaurant_data
    };

    setEditingRestaurant(restaurant);
    setEditingSavedOrder(order);
    setShowSavedOrderPopup(true);
  };

  const handleDeleteSavedOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this saved order?')) {
      return;
    }

    try {
      await savedOrdersService.deleteSavedOrder(orderId);
      setSaveStatus('Saved order deleted');
      setTimeout(() => setSaveStatus(''), 3000);
      loadSavedOrders(); // Reload list
    } catch (error) {
      logger.error('Error deleting saved order:', error);
      setSaveStatus('Error deleting order');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleSavedOrderPopupClose = () => {
    setShowSavedOrderPopup(false);
    setEditingSavedOrder(null);
    setEditingRestaurant(null);
    loadSavedOrders(); // Reload orders when popup closes
  };

  const handleFavoriteRestaurantClick = async (favorite) => {
    // Set the restaurant for the popup
    setEditingRestaurant(favorite.restaurant);
    setEditingSavedOrder(null);
    setShowSavedOrderPopup(true);
  };

  const savePreferences = async (isAutoSave = false) => {
    if (!currentUser) {
      if (!isAutoSave) {
        setSaveStatus('Error: User not logged in');
        setTimeout(() => setSaveStatus(''), 3000);
      }
      return;
    }

    try {
      const { supabase } = await import('../utils/supabase');

      const { error } = await supabase
        .from('user_profiles')
        .update({ dining_preferences: preferences })
        .eq('id', currentUser.id);

      if (error) {
        logger.error('Error saving to Supabase', { error: error.message, userId: currentUser.id });
        if (!isAutoSave) {
          setSaveStatus('Error saving preferences. Please try again.');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      } else {
        // Only show success message briefly for auto-save
        if (isAutoSave) {
          setSaveStatus('Saved');
          setTimeout(() => setSaveStatus(''), 1500);
        } else {
          setSaveStatus('Preferences saved successfully!');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      }
    } catch (error) {
      logger.error('Error saving preferences', { error: error.message, userId: currentUser.id });
      if (!isAutoSave) {
        setSaveStatus('Error saving preferences. Please try again.');
        setTimeout(() => setSaveStatus(''), 4000);
      }
    }
  };

  const addToList = (listName) => {
    if (!newItem.trim()) return;
    
    setPreferences(prev => ({
      ...prev,
      [listName]: [...prev[listName], newItem.trim()]
    }));
    
    setNewItem('');
    setAddingTo('');
  };

  const removeFromList = (listName, item) => {
    setPreferences(prev => ({
      ...prev,
      [listName]: prev[listName].filter(i => i !== item)
    }));
  };

  const handleYelpRestaurantSelect = (restaurant) => {
    setSelectedRestaurant(restaurant);
  };

  const addYelpRestaurantToList = async (listType) => {
    if (!selectedRestaurant || !currentUser) return;

    const preferenceType = listType === 'favorites' ? 'favorite' : 'avoid';
    const restaurantName = selectedRestaurant.name;

    try {
      logger.debug('Adding restaurant to list', { listType, restaurantName: selectedRestaurant.name });

      // Add to database and get the returned record
      const newFavorite = await restaurantFavoritesService.addFavorite(
        currentUser.id,
        selectedRestaurant,
        preferenceType,
        '' // notes - can add UI for this later
      );

      logger.debug('Restaurant added to database', { favoriteId: newFavorite?.id, restaurantName });

      // Optimistically update the UI immediately with the new favorite
      // This ensures instant feedback without waiting for database re-query
      const newFavoriteWithRestaurant = {
        ...newFavorite,
        restaurant: {
          id: newFavorite.restaurant_id,
          name: selectedRestaurant.name,
          address: selectedRestaurant.address,
          rating: selectedRestaurant.rating,
          price_range: selectedRestaurant.priceRange,
          review_count: selectedRestaurant.reviewCount,
          image_url: selectedRestaurant.imageUrl,
          yelp_url: selectedRestaurant.url
        }
      };

      if (preferenceType === 'favorite') {
        setFavoriteRestaurants(prev => [...prev, newFavoriteWithRestaurant]);
        logger.debug('Added to favorites state', { newCount: favoriteRestaurants.length + 1 });
      } else {
        setAvoidRestaurants(prev => [...prev, newFavoriteWithRestaurant]);
        logger.debug('Added to avoid state', { newCount: avoidRestaurants.length + 1 });
      }

      // Show success message
      setSaveStatus(`Added ${restaurantName} to ${listType}`);

      // Close modal immediately since state is already updated
      closeYelpSearch();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);

    } catch (error) {
      logger.error('Error adding restaurant', { error: error.message, restaurantName });
      setSaveStatus(`Error adding to ${listType}. Please try again.`);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const removeRestaurantFavorite = async (favoriteId) => {
    if (!currentUser) return;

    try {
      await restaurantFavoritesService.removeFavorite(currentUser.id, favoriteId);

      setSaveStatus('Restaurant removed');
      setTimeout(() => setSaveStatus(''), 2000);

      // Reload the lists
      await loadRestaurantFavorites();
    } catch (error) {
      logger.error('Error removing restaurant', { error: error.message, favoriteId });
      setSaveStatus('Error removing restaurant');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const openYelpSearch = (listType) => {
    setAddingToList(listType);
    setShowYelpSearch(true);
    setSelectedRestaurant(null);
  };

  const closeYelpSearch = () => {
    setShowYelpSearch(false);
    setAddingToList('');
    setSelectedRestaurant(null);
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading preferences...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Dining Preferences</h2>
        <p className="text-gray-600 mt-1">
          Manage your restaurant and food preferences for all meetings. (Saved Automatically)
        </p>

        {saveStatus && (
          <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
            saveStatus.includes('Error')
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {saveStatus.includes('Error') ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{saveStatus}</span>
          </div>
        )}
      </div>

      {/* Food Preferences Content */}
      <div className="space-y-6">
        {/* Restaurant Management */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Restaurant Preferences</h3>

          {/* Search Buttons */}
          <div className="mb-6 text-center py-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-sm text-gray-600 mb-3">Search for restaurants using Yelp</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => openYelpSearch('favorites')}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Heart className="w-4 h-4" />
                <span>Find Favorites</span>
              </button>
              <button
                onClick={() => openYelpSearch('avoid')}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Ban className="w-4 h-4" />
                <span>Find to Avoid</span>
              </button>
            </div>
          </div>

          {/* Restaurant Lists from Database */}
          <div className="space-y-6">
            {/* Favorite Restaurants */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Favorite Restaurants ({favoriteRestaurants.length})
              </label>
              {favoriteRestaurants.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No favorite restaurants yet. Use Yelp search above to add some!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {favoriteRestaurants.map(fav => (
                    <div
                      key={fav.id}
                      className="border border-green-200 bg-green-50 rounded-lg p-3 hover:shadow-md hover:border-green-400 transition-all cursor-pointer relative"
                      onClick={() => handleFavoriteRestaurantClick(fav)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">{fav.restaurant.name}</h4>
                            <BookMarked className="w-4 h-4 text-blue-600" />
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{fav.restaurant.address}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs">
                            {fav.restaurant.rating && (
                              <span className="text-yellow-600">⭐ {fav.restaurant.rating}</span>
                            )}
                            {fav.restaurant.price_range && (
                              <span className="text-gray-600">{fav.restaurant.price_range}</span>
                            )}
                            {fav.times_booked > 0 && (
                              <span className="text-green-600">Booked {fav.times_booked}x</span>
                            )}
                          </div>
                          <p className="text-xs text-blue-600 mt-2 font-medium">
                            Click to manage saved orders
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRestaurantFavorite(fav.restaurant_id);
                          }}
                          className="ml-2 text-red-500 hover:text-red-700"
                          title="Remove from favorites"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Restaurants to Avoid */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Restaurants to Avoid ({avoidRestaurants.length})
              </label>
              {avoidRestaurants.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No restaurants marked to avoid.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {avoidRestaurants.map(avoid => (
                    <div
                      key={avoid.id}
                      className="border border-red-200 bg-red-50 rounded-lg p-3 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{avoid.restaurant.name}</h4>
                          <p className="text-xs text-gray-600 mt-1">{avoid.restaurant.address}</p>
                          {avoid.notes && (
                            <p className="text-xs text-gray-700 mt-2 italic">Note: {avoid.notes}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeRestaurantFavorite(avoid.restaurant_id)}
                          className="ml-2 text-red-500 hover:text-red-700"
                          title="Remove from avoid list"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cuisine Preferences */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Cuisine Preferences</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Cuisines</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {preferences.cuisineTypes.map(cuisine => (
                  <span
                    key={cuisine}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {cuisine}
                    <button
                      onClick={() => removeFromList('cuisineTypes', cuisine)}
                      className="ml-2 hover:text-blue-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              {addingTo === 'cuisineTypes' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Enter cuisine type"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addToList('cuisineTypes')}
                  />
                  <button
                    onClick={() => addToList('cuisineTypes')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingTo(''); setNewItem(''); }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingTo('cuisineTypes')}
                  className="flex items-center space-x-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add cuisine</span>
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dietary Restrictions</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {preferences.dietaryRestrictions.map(restriction => (
                  <span
                    key={restriction}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800"
                  >
                    {restriction}
                    <button
                      onClick={() => removeFromList('dietaryRestrictions', restriction)}
                      className="ml-2 hover:text-orange-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              {addingTo === 'dietaryRestrictions' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Enter dietary restriction"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addToList('dietaryRestrictions')}
                  />
                  <button
                    onClick={() => addToList('dietaryRestrictions')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingTo(''); setNewItem(''); }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingTo('dietaryRestrictions')}
                  className="flex items-center space-x-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-300 hover:text-orange-600"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add restriction</span>
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Allergens to Avoid</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {preferences.allergens.map(allergen => (
                  <span
                    key={allergen}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
                  >
                    {allergen}
                    <button
                      onClick={() => removeFromList('allergens', allergen)}
                      className="ml-2 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              {addingTo === 'allergens' ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder="Enter allergen"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && addToList('allergens')}
                  />
                  <button
                    onClick={() => addToList('allergens')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setAddingTo(''); setNewItem(''); }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingTo('allergens')}
                  className="flex items-center space-x-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-300 hover:text-red-600"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add allergen</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Other Preferences */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Other Preferences</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Preference</label>
              <select
                value={preferences.deliveryPreference}
                onChange={(e) => setPreferences(prev => ({ ...prev, deliveryPreference: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Office delivery">Office delivery preferred</option>
                <option value="Restaurant pickup">Pickup at restaurant</option>
                <option value="Meet at restaurant">Meet at restaurant</option>
                <option value="No preference">No preference</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests</label>
              <textarea
                value={preferences.specialRequests}
                onChange={(e) => setPreferences(prev => ({ ...prev, specialRequests: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any special requests or notes about your dining preferences..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Yelp Restaurant Search Modal - Same as BookMeetingView */}
      {showYelpSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Find Restaurants - Adding to{' '}
                  {addingToList === 'favorites' ? (
                    <span className="text-green-600">Favorites</span>
                  ) : (
                    <span className="text-red-600">Avoid List</span>
                  )}
                </h3>
                <button
                  onClick={closeYelpSearch}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {/* Show selected restaurant and action buttons at the TOP */}
              {selectedRestaurant && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg shadow-sm sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-1">Selected Restaurant:</p>
                      <p className="font-semibold text-gray-900 text-lg">{selectedRestaurant.name}</p>
                      {selectedRestaurant.address && (
                        <p className="text-xs text-gray-600 mt-1">{selectedRestaurant.address}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedRestaurant(null)}
                      className="ml-4 p-1 text-gray-400 hover:text-gray-600"
                      title="Clear selection"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => addYelpRestaurantToList(addingToList)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-white font-medium ${
                        addingToList === 'favorites'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {addingToList === 'favorites' ? (
                        <>
                          <Heart className="w-4 h-4" />
                          <span>Add to Favorites</span>
                        </>
                      ) : (
                        <>
                          <Ban className="w-4 h-4" />
                          <span>Add to Avoid</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={closeYelpSearch}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Yelp Search Component */}
              <YelpRestaurantSearch
                onRestaurantSelect={handleYelpRestaurantSelect}
                selectedLocation={(() => {
                  // Get location from currentUser profile
                  const location =
                    currentUser?.city && currentUser?.state
                      ? `${currentUser.city}, ${currentUser.state}`
                      : currentUser?.address || '';
                  logger.debug('Customer location for Yelp search', { location, userData: {
                    city: currentUser?.city,
                    state: currentUser?.state,
                    address: currentUser?.address
                  }});
                  return location;
                })()}
                placeholder="Search for restaurants on Yelp..."
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* Saved Order Popup */}
      <SavedOrderPopup
        isOpen={showSavedOrderPopup}
        onClose={handleSavedOrderPopupClose}
        restaurant={editingRestaurant}
        savedOrder={editingSavedOrder}
        customerId={currentUser?.id}
        onSave={loadSavedOrders}
      />
    </div>
  );
};

export default DiningPreferencesView;